/**
 * ScanIdCardButton — wizard-friendly wrapper around CameraCaptureDialog + IOcrProvider.
 *
 * Story 70-1E-a (E-70 OCR — wizard PartnerStep + PartnerCreate + QuickCreate).
 *
 * Flow:
 *   1. User clicks teal "📷 Igazolvány szkennelés" button
 *   2. <CameraCaptureDialog> opens (Story 70-1D shipped)
 *   3. On {front, back} captured → IOcrProvider.extract(back) — MRZ on back
 *      (front fallback if back fails — Hungarian "szig" has MRZ on back)
 *   4. On success → onScanned(ocrResult.fields) so parent prefills
 *   5. On failure → HU toast error
 *
 * Provider injection (ADR-104):
 *   - Default: InMemoryOcrProvider (canned result, used in dev + tests until
 *     TesseractOcrProvider WASM bundle ships in story 70-1C/D)
 *   - Production: parent passes real provider via `provider` prop OR module
 *     boundary swap once TesseractOcrProvider/browser entry is built
 *
 * The provider is intentionally kept as a prop so test harnesses can pass
 * `InMemoryOcrProvider` with `failNextExtract({...})` to drive UI states.
 */

import { CameraCaptureDialog } from '@/components/partners/CameraCaptureDialog';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@kgc/i18n';
import {
  InMemoryOcrProvider,
  type ExtractedIdFields,
  type IOcrProvider,
  type OcrResult,
} from '@kgc/tesseract-provider';
import { Button, toast as sonnerToast } from '@kgc/ui';
import { useCallback, useMemo, useState } from 'react';

const TEAL = '#14B8A6';

export interface ScanIdCardButtonProps {
  /**
   * Called when OCR succeeds (T1 or T2 confidence). T3 still fires but the
   * caller is warned via toast that confidence was low.
   */
  onScanned: (fields: ExtractedIdFields, full: OcrResult) => void;
  /** Optional provider override — defaults to a ready InMemoryOcrProvider. */
  provider?: IOcrProvider;
  /** Render as outline (lighter visual) instead of filled teal. */
  variant?: 'filled' | 'outline';
  /** Forwarded to `<button data-testid>` for E2E selectors. */
  'data-testid'?: string;
  /** Disable the button (e.g. while parent form is submitting). */
  disabled?: boolean;
  /** Optional className additions on the trigger button. */
  className?: string;
}

export function ScanIdCardButton({
  onScanned,
  provider,
  variant = 'filled',
  disabled = false,
  className,
  'data-testid': dataTestId,
}: ScanIdCardButtonProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Default provider: InMemoryOcrProvider (autoReady) — swap to TesseractOcrProvider
  // once browser WASM bundle ships in story 70-1C/D (ADR-104 D-3 deferred load).
  const ocr = useMemo<IOcrProvider>(
    () => provider ?? new InMemoryOcrProvider({ autoReady: true }),
    [provider]
  );

  const handleCaptured = useCallback(
    async ({ front, back }: { front: Blob; back: Blob }): Promise<void> => {
      setBusy(true);
      let result: OcrResult | null = null;
      try {
        if (!ocr.isReady()) {
          await ocr.initialize();
        }
        // Hungarian szig MRZ is on the BACK side. Try back first; on parse failure
        // fall back to front (passport / EU ID may have MRZ on data page front).
        try {
          result = await ocr.extract(back);
        } catch (backErr) {
          // Defensive: log + try front as fallback. Don't surface back error.
          console.warn('[ScanIdCardButton] back-side OCR failed, retrying front', backErr);
          result = await ocr.extract(front);
        }
      } catch (err) {
        console.error('[ScanIdCardButton] OCR extraction failed', err);
        toast({
          title: t('partner.scan.failure'),
          variant: 'destructive',
        });
        setBusy(false);
        return;
      }

      // Defense-in-depth — even though caller owns Blob lifetime, we drop our
      // refs immediately so V8 can GC the image bytes.
      // (CameraCaptureDialog already revokes thumbnails on close.)
      // The Blob references die when this callback returns.

      if (!result) {
        toast({
          title: t('partner.scan.failure'),
          variant: 'destructive',
        });
        setBusy(false);
        return;
      }

      if (result.tier === 'T3') {
        // Still call onScanned so caller can decide; UI just warns.
        sonnerToast.warning(t('partner.scan.lowConfidence'));
      } else {
        sonnerToast.success(t('partner.scan.success'));
      }
      onScanned(result.fields, result);
      setBusy(false);
    },
    [ocr, onScanned, t, toast]
  );

  const isFilled = variant === 'filled';

  return (
    <>
      <Button
        type="button"
        variant={isFilled ? 'default' : 'outline'}
        disabled={disabled || busy}
        onClick={() => setOpen(true)}
        aria-label={t('partner.scan.buttonAriaLabel')}
        data-testid={dataTestId ?? 'scan-id-card-button'}
        className={className}
        {...(isFilled
          ? { style: { backgroundColor: TEAL, borderColor: TEAL, color: '#ffffff' } }
          : { style: { borderColor: TEAL, color: TEAL } })}
      >
        {busy ? t('partner.scan.processing') : t('partner.scan.button')}
      </Button>
      <CameraCaptureDialog
        open={open}
        onOpenChange={setOpen}
        onCaptured={images => {
          void handleCaptured(images);
        }}
      />
    </>
  );
}
